/**
 * First-party marketing attribution cookies (DAT-55).
 *
 * Two cookies on the registrable parent domain (`.thebrief.ai`, `.thebrief.dev`,
 * `.creatopy.com`), 90-day expiry, so they survive the www → auth → app hops:
 *
 * - `tb_first_touch`: written once, on the very first landing on any page —
 *   campaign params when present, plus referrer, landing page and timestamp
 *   (an organic first touch is still a first touch).
 * - `tb_last_touch`: rewritten on every campaign-tagged visit.
 *
 * They are the durable, consent-independent copy of what the cookie-handler /
 * Redis session pipeline also captures; the app reads them as a fallback and
 * sends them with the sign_up event and the account-creation mutation. Values
 * are marketing params only — the referrer is stripped to origin + path so no
 * query-string PII ends up here. Whether these may be written before consent
 * is a privacy-review question, see apps/graphql/src/services/attribution/README.md.
 */ export const FIRST_TOUCH_COOKIE = 'tb_first_touch';
export const LAST_TOUCH_COOKIE = 'tb_last_touch';
export const TOUCH_COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;
const CAMPAIGN_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'gclid',
    'msclkid',
    'fbclid'
];
const IPV4 = /^\d{1,3}(\.\d{1,3}){3}$/;
function registrableDomain(hostname) {
    const labels = hostname.split('.').filter(Boolean);
    if (labels.length < 2 || IPV4.test(hostname)) {
        return null;
    }
    return labels.slice(-2).join('.');
}
/** `.thebrief.ai` for any `*.thebrief.ai` host; null for localhost / IPs (host-only cookie). */ export function cookieDomainFor(hostname) {
    const domain = registrableDomain(hostname);
    return domain ? `.${domain}` : null;
}
function originAndPath(url) {
    return `${url.origin}${url.pathname}`;
}
/** Campaign params (case-insensitive), external referrer without its query, landing page and timestamp. */ export function parseTouch(href, referrer, now) {
    const url = new URL(href);
    const params = new Map();
    url.searchParams.forEach((value, key)=>{
        const lower = key.toLowerCase();
        if (value && !params.has(lower)) {
            params.set(lower, value);
        }
    });
    const touch = {
        timestamp: now.toISOString()
    };
    for (const key of CAMPAIGN_KEYS){
        const value = params.get(key);
        if (value) {
            touch[key] = value;
        }
    }
    if (referrer) {
        try {
            const referrerUrl = new URL(referrer);
            if (registrableDomain(referrerUrl.hostname) !== registrableDomain(url.hostname)) {
                touch.referrer = originAndPath(referrerUrl);
            }
        } catch  {
        // not a URL: ignore
        }
    }
    touch.landing_page = originAndPath(url);
    return touch;
}
/** True when the touch carries any utm or ad click id (referrer / landing page alone do not count). */ export function hasCampaignSignal(touch) {
    return CAMPAIGN_KEYS.some((key)=>Boolean(touch[key]));
}
export function serializeTouchCookie(name, touch, hostname) {
    const parts = [
        `${name}=${encodeURIComponent(JSON.stringify(touch))}`
    ];
    const domain = cookieDomainFor(hostname);
    if (domain) {
        parts.push(`Domain=${domain}`);
    }
    parts.push('Path=/', `Max-Age=${TOUCH_COOKIE_MAX_AGE_SECONDS}`, 'SameSite=Lax');
    if (hostname !== 'localhost') {
        parts.push('Secure');
    }
    return parts.join('; ');
}
function parseTouchValue(raw) {
    try {
        const parsed = JSON.parse(decodeURIComponent(raw));
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            const touch = parsed;
            if (typeof touch.timestamp === 'string') {
                return touch;
            }
        }
    } catch  {
    // corrupt cookie: ignore
    }
    return undefined;
}
/** Tolerant reader: a missing or corrupt cookie is simply absent. */ export function readTouchCookies(cookieString) {
    const cookies = {};
    for (const part of cookieString.split(';')){
        const trimmed = part.trim();
        const separator = trimmed.indexOf('=');
        if (separator < 0) {
            continue;
        }
        const name = trimmed.slice(0, separator);
        const value = trimmed.slice(separator + 1);
        if (name === FIRST_TOUCH_COOKIE) {
            const touch = parseTouchValue(value);
            if (touch) {
                cookies.first = touch;
            }
        } else if (name === LAST_TOUCH_COOKIE) {
            const touch = parseTouchValue(value);
            if (touch) {
                cookies.last = touch;
            }
        }
    }
    return cookies;
}
/** Pure planning step: what the first/last touch are after this page view, and which cookies to write. */ export function planTouchCookies({ href, referrer, cookieString, now }) {
    const hostname = new URL(href).hostname;
    const existing = readTouchCookies(cookieString);
    const touch = parseTouch(href, referrer, now);
    const writes = [];
    let first = existing.first;
    if (!first) {
        first = touch;
        writes.push(serializeTouchCookie(FIRST_TOUCH_COOKIE, touch, hostname));
    }
    let last = existing.last;
    if (hasCampaignSignal(touch)) {
        last = touch;
        writes.push(serializeTouchCookie(LAST_TOUCH_COOKIE, touch, hostname));
    }
    return {
        first,
        last,
        writes
    };
}
/** Browser entry point: record this page view in the touch cookies. Never throws. */ export function recordTouchCookies() {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return {};
    }
    try {
        const plan = planTouchCookies({
            href: window.location.href,
            referrer: document.referrer,
            cookieString: document.cookie,
            now: new Date()
        });
        for (const cookie of plan.writes){
            document.cookie = cookie;
        }
        return {
            first: plan.first,
            last: plan.last
        };
    } catch  {
        return getTouchCookies();
    }
}
/** Browser reader for the current touch cookies. Never throws. */ export function getTouchCookies() {
    if (typeof document === 'undefined') {
        return {};
    }
    try {
        return readTouchCookies(document.cookie);
    } catch  {
        return {};
    }
}

//# sourceMappingURL=touchCookies.js.map